import clsx from 'clsx';
import { CSSProperties, ReactElement } from 'react';
import { useMemo } from 'react';

import { Padding } from '../model';
import { Banner } from './Banner';
import { BorderContainer } from './BorderContainer';
import style from './Footer.module.css';
import { Horizontal, PopoverPosition, Vertical } from './Popover';
import { Privacy } from './Privacy';

export interface FooterProps {
  showBanner: boolean;
  showPrivacyLink: boolean;
  rightActions?: ReactElement[] | undefined | null;
  fullWidth?: boolean;
  buttonColor?: string;
  bordered?: boolean;
  privacyPosition?: PopoverPosition;
  customPrivacyURL?: string;
  padding?: Padding;
}

export const Footer = ({
  showBanner,
  showPrivacyLink,
  rightActions,
  fullWidth,
  buttonColor,
  bordered,
  privacyPosition,
  customPrivacyURL,
  padding,
}: FooterProps) => {
  const rootCSS = useMemo(() => {
    if (!padding) {
      return undefined;
    }

    const newProps: CSSProperties = {};
    const { top, left, right, bottom } = padding;
    if (top !== undefined) {
      newProps.marginTop = top;
    }
    if (bottom !== undefined) {
      newProps.marginBottom = bottom;
    }
    if (left !== undefined) {
      newProps.marginLeft = left;
    }
    if (right !== undefined) {
      newProps.marginRight = right;
    }
    return newProps;
  }, [padding]);

  const leftActions = [];

  if (showPrivacyLink) {
    leftActions.push(
      <div>
        <Privacy
          popoverBackground="var(--color-bg-secondary)"
          position={privacyPosition ?? { horizontal: Horizontal.left, vertical: Vertical.top }}
          buttonColor={buttonColor}
          customPrivacyURL={customPrivacyURL}
        />
      </div>
    );
  }

  const banner = showBanner ? <Banner /> : null;
  const hasBanner = banner !== null;

  const hasRightActions = rightActions && rightActions.length > 0;
  const hasLeftActions = leftActions && leftActions.length > 0;
  if (!hasBanner && !hasLeftActions && !hasRightActions) {
    return <></>;
  }

  const leftActionsMarkup =
    leftActions.length > 0 ? (
      <div className={clsx(style.actions, { [style.left]: hasRightActions || hasBanner }, { [style.actions_single]: leftActions.length === 1 })}>
        {leftActions}
      </div>
    ) : null;

  const rightActionsMarkup =
    hasRightActions || hasBanner ? (
      <div
        className={clsx(
          style.actions,
          { [style.right]: leftActions.length > 0 },
          { [style.actions_single]: rightActions && rightActions.length === 1 }
        )}
      >
        {rightActions}
      </div>
    ) : null;

  const footerContent = (
    <div className={clsx(style.footer, { [style.withBanner]: hasBanner }, { [style.fullWidth]: fullWidth })} style={rootCSS}>
      {leftActionsMarkup}
      {banner}
      {rightActionsMarkup}
    </div>
  );

  if (bordered) {
    return (
      <BorderContainer topPadding="4px" fullWidth={fullWidth} borderHidden={!bordered}>
        {footerContent}
      </BorderContainer>
    );
  }

  return footerContent;
};
